/* ============================================================
   Vincent Peach — Wishlist storage engine
   localStorage-based (persists on this browser/device, same
   practical behavior as a guest cart). Single source of truth
   used by: product pages, category grid hearts, and the header
   wishlist dialog.

   Storage shape: array of objects, e.g.
   [{ id: "12345", title: "...", price: "...", image: "...", url: "..." }]
   ============================================================ */
window.VPWishlist = (function(){
  var KEY = 'vp_wishlist';

  function getAll(){
    try{
      var raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    }catch(e){ return []; }
  }

  function save(items){
    try{
      localStorage.setItem(KEY, JSON.stringify(items));
    }catch(e){}
    broadcast();
  }

  function has(id){
    return getAll().some(function(item){ return item.id === id; });
  }

  function add(item){
    var items = getAll();
    if(items.some(function(i){ return i.id === item.id; })) return;
    items.push(item);
    save(items);
  }

  function remove(id){
    var items = getAll().filter(function(i){ return i.id !== id; });
    save(items);
  }

  function toggle(item){
    if(has(item.id)) remove(item.id);
    else add(item);
    return has(item.id);
  }

  function count(){
    return getAll().length;
  }

  function broadcast(){
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: getAll() } }));
  }

  return { getAll: getAll, add: add, remove: remove, toggle: toggle, has: has, count: count };
})();

/* ============================================================
   Header wishlist icon integration
   Finds the header's wishlist icon using several possible
   selectors (robust to whatever attribute your header actually
   uses), adds/updates a count badge, and opens the wishlist
   dialog on click instead of any old hover-popup behavior.
   ============================================================ */
(function(){
  function findWishTrigger(){
    return document.querySelector('[data-vp-wish-toggle]')
        || document.querySelector('[data-vp-wish]')
        || document.querySelector('a[aria-label="Wishlist"]')
        || document.querySelector('button[aria-label="Wishlist"]');
  }

  function updateBadge(){
    var trigger = findWishTrigger();
    if(!trigger || !window.VPWishlist) return;
    var count = window.VPWishlist.count();
    var badge = trigger.querySelector('.vp-wish-count');
    if(count > 0){
      if(!badge){
        badge = document.createElement('span');
        badge.className = 'vp-wish-count';
        /* styled in assets/vp-header.css next to .vp-bag-count, so the two
           badges share one placement per breakpoint */
        if(getComputedStyle(trigger).position === 'static') trigger.style.position = 'relative';
        trigger.appendChild(badge);
      }
      badge.textContent = count;
    } else if(badge){
      badge.remove();
    }
  }

  function initHeaderTrigger(){
    var trigger = findWishTrigger();
    if(!trigger || trigger.dataset.wishDialogInit) return;
    trigger.dataset.wishDialogInit = '1';

    trigger.addEventListener('click', function(e){
      e.preventDefault();
      e.stopPropagation();
      var dialog = document.getElementById('vp-wishlist-dialog');
      if(dialog && typeof dialog.__openWishlistDialog === 'function'){
        dialog.__openWishlistDialog();
      }
    }, true); // capture phase — overrides any existing hover-popup click handler

    updateBadge();
  }

  document.addEventListener('DOMContentLoaded', initHeaderTrigger);
  document.addEventListener('shopify:section:load', initHeaderTrigger);
  document.addEventListener('wishlist:change', updateBadge);
  if(document.readyState !== 'loading') initHeaderTrigger();
})();

/* ============================================================
   Wishlist controls — one delegated handler for every page
   ------------------------------------------------------------
   Each section used to wire its own heart, and most of them only
   toggled a CSS class ("visual affordance only"), so whether a
   save actually persisted depended on which page you were on.
   The wiring lives here now: one capture-phase listener owns
   every control on the site, so a heart works the moment its
   markup is rendered -- including sections loaded later by the
   theme editor -- and no section has to remember to repeat it.

   A control is any element carrying [data-wish] or
   [data-vp-piece-wish]. It may describe its piece outright:

     data-wish-id / -title / -price / -image / -url

   Anything not given is read off the surrounding card, which is
   how the grid hearts work -- they sit inside the card and have
   its heading, price and image to hand.

   Search results are the one exception: vp-search.js builds its
   hearts in JS and wires them as it goes, and its buttons carry
   neither attribute, so they never reach this handler.
   ============================================================ */
(function(){
  var CONTROL = '[data-wish],[data-vp-piece-wish]';
  var CARD    = '.plate,.piece,.vp-piece,article,li';
  var TITLE   = '[data-wish-title],h1,h2,h3,h4';
  var PRICE   = '[data-wish-price],.plate-price,.piece-price,.vp-piece-price,.price';

  function text(el){
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function itemFor(control){
    var card  = control.closest(CARD);
    /* the heart normally sits inside the card's own link; when it does not
       (the product page's "Save to Wishlist"), fall back to the card's */
    var link  = control.closest('a[href]') || (card && card.querySelector('a[href]'));
    /* first image in DOM order is the resting one in every card here -- the
       packshot on grid plates, the featured shot on "Complete the Look" --
       with the hover image second. */
    var img   = card && card.querySelector('img');
    var title = control.getAttribute('data-wish-title')
             || control.getAttribute('data-name')
             || text(card && card.querySelector(TITLE));
    var url   = control.getAttribute('data-wish-url')
             || (link ? link.getAttribute('href') : '');

    return {
      id: idFor(control, card, url, title),
      title: title,
      price: control.getAttribute('data-wish-price')
          || text(card && card.querySelector(PRICE)),
      image: control.getAttribute('data-wish-image')
          || (img ? (img.currentSrc || img.src) : ''),
      url: url || '#'
    };
  }

  /* Identity is whatever the markup declares, never the link on its own:
     the cards in one cluster often point at the same page, so keying on the
     href alone gave six pieces one id -- saving one lit them all up and the
     next click removed the first. Sections that render real products set
     data-wish-id to the product id; authored blocks set section-block. The
     positional fallback is only for a control that declares neither an id
     nor a link of its own. */
  function idFor(control, card, url, title){
    var declared = control.getAttribute('data-wish-id')
                || (card && card.getAttribute('data-wish-id'));
    if(declared) return declared;
    if(url && url !== '#') return url;
    var all = document.querySelectorAll(CONTROL);
    return (title || 'piece') + '-' + Array.prototype.indexOf.call(all, control);
  }

  /* Sections style their saved state differently -- .saved in the clusters
     and "Complete the Look", .active in the Collector's Edit, aria-pressed
     on the product page's button. Setting all three keeps every existing
     stylesheet working untouched; aria-pressed is left to real buttons,
     since the grid hearts are spans and it would mean nothing on them. */
  function reflect(control, saved){
    control.classList.toggle('saved', saved);
    control.classList.toggle('active', saved);
    if(control.tagName === 'BUTTON' || control.getAttribute('role') === 'button'){
      control.setAttribute('aria-pressed', saved ? 'true' : 'false');
    }
  }

  function syncAll(){
    if(!window.VPWishlist) return;
    document.querySelectorAll(CONTROL).forEach(function(control){
      reflect(control, window.VPWishlist.has(itemFor(control).id));
    });
  }

  /* Capture, so the click is claimed before the card's own handlers run:
     the heart sits inside the product link on most cards, and some sections
     stop propagation on their card clicks. */
  document.addEventListener('click', function(e){
    var control = e.target.closest && e.target.closest(CONTROL);
    if(!control) return;
    e.preventDefault();
    e.stopPropagation();
    if(!window.VPWishlist) return;
    reflect(control, window.VPWishlist.toggle(itemFor(control)));
  }, true);

  document.addEventListener('DOMContentLoaded', syncAll);
  document.addEventListener('shopify:section:load', syncAll);
  document.addEventListener('wishlist:change', syncAll);
  if(document.readyState !== 'loading') syncAll();
})();
